// src/hooks/useConversation.ts
import { useCallback, useEffect, useState } from 'react';
import { HistoryMessage } from '../services/openai';

export interface ConversationMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  streaming?: boolean;
  timestamp: number;
}

interface UseConversationReturn {
  messages: ConversationMessage[];
  addUserMessage: (content: string) => string;
  addAssistantMessage: (content: string) => string;
  updateAssistantMessage: (id: string, content: string) => void;
  finalizeAssistantMessage: (id: string) => void;
  clear: () => void;
  getHistory: () => HistoryMessage[];
}

export function useConversation(): UseConversationReturn {
  // Load messages from localStorage lazily so the initial state already
  // contains any persisted conversation (avoids wiping it on first save).
  const [messages, setMessages] = useState<ConversationMessage[]>(() => {
    const saved = localStorage.getItem('conversation_messages');
    if (saved) {
      try {
        return JSON.parse(saved) as ConversationMessage[];
      } catch (e) {
        console.error('Failed to load messages:', e);
      }
    }
    return [];
  });

  // Save messages to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('conversation_messages', JSON.stringify(messages));
    } catch (e) {
      console.error('Failed to save messages:', e);
    }
  }, [messages]);

  const addUserMessage = useCallback((content: string) => {
    const id = `user-${Date.now()}`;
    const newMessage: ConversationMessage = {
      id,
      role: 'user',
      content,
      timestamp: Date.now(),
    };
    setMessages(prev => [...prev, newMessage]);
    return id;
  }, []);

  const addAssistantMessage = useCallback((content: string) => {
    const id = `assistant-${Date.now()}`;
    const newMessage: ConversationMessage = {
      id,
      role: 'assistant',
      content,
      streaming: true,
      timestamp: Date.now(),
    };
    setMessages(prev => [...prev, newMessage]);
    return id;
  }, []);

  const updateAssistantMessage = useCallback((id: string, content: string) => {
    setMessages(prev =>
      prev.map(msg =>
        msg.id === id ? { ...msg, content, streaming: true } : msg
      )
    );
  }, []);

  const finalizeAssistantMessage = useCallback((id: string) => {
    setMessages(prev =>
      prev.map(msg =>
        msg.id === id ? { ...msg, streaming: false } : msg
      )
    );
  }, []);

  const clear = useCallback(() => {
    setMessages([]);
    localStorage.removeItem('conversation_messages');
  }, []);

  const getHistory = useCallback((): HistoryMessage[] => {
    return messages
      .filter(m => !m.streaming && m.content.trim().length > 0)
      .map(m => ({ role: m.role, content: m.content }));
  }, [messages]);

  return {
    messages,
    addUserMessage,
    addAssistantMessage,
    updateAssistantMessage,
    finalizeAssistantMessage,
    clear,
    getHistory,
  };
}